/**
 * Central error sanitization layer.
 *
 * Prevents raw backend/internal error messages from reaching end users.
 * Preserves legitimate user-facing validation messages while replacing
 * technical/internal messages with a generic fallback.
 */

export const GENERIC_ERROR_MSG = 'Ocurrió un error. Intentá nuevamente.'

/**
 * Map of known backend error codes to user-friendly Spanish messages.
 * A null value means "suppress and show generic" (not actionable for the user).
 */
const FRIENDLY_CODE_MAP = {
  TENANT_ID_REQUIRED: null,
  COMPANY_NOT_FOUND: null,
  COMPANY_INACTIVE: 'La empresa no está activa. Contactá al administrador.',
  ACCESS_DENIED: 'No tenés permiso para esta acción.',
  ROLE_NOT_ALLOWED: 'No tenés permiso para esta acción.',
  FORBIDDEN: 'No tenés permiso para esta acción.',
  INVALID_TOKEN: null,
  TOKEN_EXPIRED: null,
}

/**
 * Patterns that indicate a message is internal/technical.
 * Any message matching one of these is replaced with the generic fallback.
 */
const TECHNICAL_PATTERNS = [
  /header\s+x-/i,                                                  // HTTP header names (x-company-tenant-id, x-*)
  /^error\s+[45]\d{2}\s*$/i,                                       // Bare "Error 400" / "Error 500" strings
  /\binternal\s+server\s+error\b/i,                                // "Internal server error" variants
  /(TypeError|ReferenceError|SyntaxError|FetchError|AxiosError)/,  // JS error class names
  /\b(SELECT|INSERT|UPDATE|DELETE|DROP)\b.{0,60}\b(FROM|INTO|TABLE|WHERE)\b/i, // SQL fragments
  /stack\s+trace|at\s+Object\.\s*<|at\s+async\s+\w/i,             // Stack trace indicators
  /at\s+\S+\.js:\d+/i,                                             // JS source file references
  /\bsql\b.*\berror\b/i,                                           // Generic SQL error messages
]

/**
 * Returns a user-safe version of a backend error message.
 *
 * - If a known `code` is mapped, returns the mapped message (or generic if mapped to null).
 * - If the message matches a technical pattern, returns the generic fallback.
 * - Otherwise, returns the original message unchanged.
 *
 * @param {string} message - Raw error message string
 * @param {string|null} [code] - Optional backend error code (e.g. "TENANT_ID_REQUIRED")
 * @returns {string} A user-safe message string
 */
export function sanitizeErrorMessage(message, code = null) {
  if (!message || typeof message !== 'string') return GENERIC_ERROR_MSG

  // 1. Known-code mapping takes priority over pattern matching
  if (code && Object.prototype.hasOwnProperty.call(FRIENDLY_CODE_MAP, code)) {
    const friendly = FRIENDLY_CODE_MAP[code]
    return friendly != null ? friendly : GENERIC_ERROR_MSG
  }

  // 2. Technical pattern detection on the raw message string
  for (const pattern of TECHNICAL_PATTERNS) {
    if (pattern.test(message)) return GENERIC_ERROR_MSG
  }

  return message
}
